import { Component, Directive, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { CommonService } from '@app/core';
import { utcToTimezone } from '@app/helpers/timezone-handler';
import { ScheduleData, TimeData } from '@app/models';
import { Timezone } from '@app/models/organization.model';
import { MOMENT_DATE_FORMAT, MOMENT_EVENT_DATE_FORMATS } from '@app/shared';
import * as _moment from 'moment';
const moment = _moment;

@Directive({
    selector: '[shortDateFormat]',
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MOMENT_DATE_FORMAT },
    ],
    standalone: false
})
export class CustomShortDateFormatDirective {}
@Component({
    selector: 'app-scheduler-fields',
    templateUrl: './scheduler-fields.component.html',
    styleUrls: ['./scheduler-fields.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS, useValue: MOMENT_EVENT_DATE_FORMATS }
    ],
    standalone: false
})
export class SchedulerFieldsComponent implements OnInit {

  private readonly DEFAULT_TIME_INTERVAL = 15;

  @Input('formClass') formClass: string = '';
  @Input('dateFormat') dateFormat: 'short' | 'full' = 'full';
  @Input('scheduleValues') scheduleValues: ScheduleData | null = null;
  @Input('orgTimeZone') orgTimeZone!: Timezone;
  @Input('scheduledTime') scheduledTime: string | null = null;
  @Input('disabled') disabled: boolean = false;
  @Input('timeIntervalMinutes') timeIntervalMinutes: number = this.DEFAULT_TIME_INTERVAL;
  @Output('valueChanges') valueChanges: EventEmitter<any> = new EventEmitter<any>();
  scheduleForm!: UntypedFormGroup;  
  timeData: TimeData[] = [];
  minDate: _moment.Moment = moment();

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.timeData = this._commonService.generateTimeData(this.timeIntervalMinutes);
    this.createForm();
  }

  createForm(timestampData?: string) {
      if (timestampData) this.scheduledTime = timestampData;
      this.minDate = moment.tz(this.orgTimeZone.timezone_identifier);
      if (this.scheduledTime) {
        let startdatetime = utcToTimezone(this.scheduledTime, this.orgTimeZone.timezone_identifier);
        this.scheduleValues = {
          date: startdatetime,
          time: this.getEventTimeData(startdatetime)
        };
      } else if ((this.minDate.hours() == 23) && (this.minDate.minutes() >= 38)) {
        this.minDate = this.minDate.add(1, 'day').startOf('day');
        this.scheduleValues = {
          date: this.minDate,
          time: this.timeData[0]
        };
      }
      this.scheduleForm = this._formBuilder.group({
        date: this._formBuilder.control({value: (this.scheduleValues?.date || this.minDate), disabled: this.disabled }),
        time: this._formBuilder.control({
          value: (this.scheduleValues?.time || this.getTimeFromCurrentTime(this.DEFAULT_TIME_INTERVAL)), 
          disabled: this.disabled 
        }, Validators.required),
      });
    this.emitChanges();
  }

  /**get Current time from system
   * roundup time to match required options
   */
  getTimeFromCurrentTime(addMins: number) {
    let currentDateTime = moment.tz(this.orgTimeZone.timezone_identifier);
    let currentHrs = currentDateTime.hours();
    let mins = currentDateTime.minutes();
    const step = this.timeIntervalMinutes > 0 ? this.timeIntervalMinutes : this.DEFAULT_TIME_INTERVAL;
    let currentMins = mins % step === 0 ? mins : Math.round(mins / step) * step;
    currentMins += addMins;

    if (currentMins === 60) {
      currentMins = 0;
      currentHrs += 1;
      if (currentHrs === 24) currentHrs = 0;
    } else if (currentMins > 60) {
      currentMins = currentMins - 60;
      currentHrs += 1;
    }

    return this.timeData.find(td => td.hours == currentHrs && td.minutes == currentMins);
  }

  /**
   * Combine the date and time for event start and end
   * @param date 
   * @param time 
   * @returns COmbined DateTime in string
   */
  getCombineDateTime(): moment.Moment {
    let dateValue = this.scheduleForm.value.date;
    let timeValue = this.scheduleForm.value.time;
    let dateTimeStr = dateValue.startOf('day');
    
    let momentDatetime = moment.tz(
      [dateTimeStr.year(), dateTimeStr.month(), dateTimeStr.date(), timeValue.hours, timeValue.minutes], 
      this.orgTimeZone.timezone_identifier
    );
    return momentDatetime.utc();
  }

  getEventTimeData(time: moment.Moment): TimeData {
    let _hours: number = time.hours();
    let _minutes: number = time.minutes();
    return this.timeData.find(timeData => (timeData.hours === _hours && timeData.minutes === _minutes)) 
      || new TimeData(this.timeData[0].label, this.timeData[0].hours, this.timeData[0].minutes);
  }

  emitChanges() {
    this.valueChanges.emit();
  }

  /**
   * Restrict User from writing own in the input field.
   * @returns False
   */
  restrictInput(): boolean {
    return false;
  }

  get hasChanges() {
    return this.scheduleForm?.dirty || null;
  }

}
